import React, { memo, useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Header from '../../components/atoms/Header'
import Text from '../../components/atoms/Text'
import Button from '../../components/atoms/GoodFlipButton'
import ArrowIcon from '../../components/atoms/Arrow'
import Lottie from 'lottie-react'
import PersonalisedLabTest from '../../assets/images/personalised-lab-test.webp'
import BookDiagnosticBridge from '../../components/organisms/BookDiagonosticBridge'
import * as Separator from '../../assets/animations/separator.json'
import { formatCurrency } from '../../utils/helpers/common'
import LoginBottomSheet from '../../components/molecules/LoginBottomSheet'
import { useSelector } from 'react-redux'
import MobileLayout from '../../components/MobileLayout'
import { trackEventCKD } from '../../analytics'
import { EVENTS } from '../../analytics/events'

const LabTestIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M22.2805 8.09448L16.6555 2.46948C16.5859 2.39974 16.5031 2.34442 16.4121 2.30668C16.321 2.26894 16.2234 2.24951 16.1249 2.24951C16.0263 2.24951 15.9287 2.26894 15.8377 2.30668C15.7466 2.34442 15.6639 2.39974 15.5943 2.46948L3.47895 14.5848C2.69175 15.372 2.24951 16.4397 2.24951 17.5529C2.24951 18.6662 2.69175 19.7338 3.47895 20.521C4.26615 21.3082 5.33381 21.7505 6.44707 21.7505C7.56034 21.7505 8.628 21.3082 9.4152 20.521L19.9049 10.0314L21.9871 9.3376C22.1084 9.29723 22.2175 9.2265 22.3038 9.13211C22.3901 9.03773 22.4509 8.92284 22.4803 8.79836C22.5097 8.67388 22.5068 8.54394 22.4718 8.42091C22.4368 8.29787 22.371 8.18583 22.2805 8.09541V8.09448ZM19.2627 8.66354C19.1522 8.70025 19.0517 8.7622 18.9693 8.84448L15.2596 12.5542C14.4655 12.826 13.2205 12.9292 11.5968 12.0854C10.603 11.566 9.70489 11.3354 8.91926 11.267L16.1249 4.06041L20.3615 8.29698L19.2627 8.66354Z"
      fill="#101828"
    />
  </svg>
)

const DiagnosticPage = () => {
  const [showFooter2, setShowFooter2] = useState(false)
  const loginSheetRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()

  const query = new URLSearchParams(location.search)
  const [comingFromButton, setComingFromButton] = useState(
    query.get('fromButtonPress') ?? 'false',
  )
  const { isAuthorized } = useSelector((s) => s.auth)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY
      const pageHeight = document.documentElement.scrollHeight

      if (scrollPosition >= pageHeight - 10) {
        setShowFooter2(true)
      } else {
        setShowFooter2(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    console.log('trackEventCKD --> CKD_LKF_Book_Diagnostics_Landing')
    trackEventCKD(EVENTS.CKD_LKF_Book_Diagnostics_Landing)
  }, [])

  useEffect(() => {
    if (!isAuthorized && comingFromButton === 'true') {
      loginSheetRef.current?.show()
    }
  }, [isAuthorized, comingFromButton])

  const onClickBookDiagnostics = () => {
    navigate('/purchase-diagnostic')
  }
  return (
    <MobileLayout>
      {comingFromButton === 'false' ? (
        <BookDiagnosticBridge
          onClick={() => {
            setComingFromButton('true')
          }}
        />
      ) : (
        <div className="relative min-h-screen">
          <Header
            title="Back"
            onPressLeftIcon={() => {
              navigate(-1)
            }}
          />
          <div className="p-4 lg:max-w-2xl lg:mx-auto">
            <Text
              tag={'p'}
              variant={'headline'}
              size={'medium'}
              className={'text-text-primary'}
            >
              Understand yourself better with data
            </Text>
            <Text
              tag={'p'}
              variant={'title'}
              size={'large'}
              className={'text-text-tertiary mt-4'}
            >
              Your next steps:
            </Text>
            <div className="flex items-center justify-center py-2">
              <img
                src={PersonalisedLabTest}
                alt="personalised-lab-test"
                className="h-[200px] w-[200px] lg:h-[260px] lg:w-[260px]"
              />
            </div>
            <div className="border-b border-border-secondary space-y-1 mt-6 flex items-center gap-6 mx-10 pb-2">
              <div>
                <Text tag="p" variant="label" size="medium">
                  Price for you
                </Text>
                <div className="flex items-center gap-x-2.5">
                  <Text
                    tag="p"
                    variant="title"
                    size="large"
                    className="text-text-success-primary"
                  >
                    {formatCurrency(599)}
                  </Text>
                  <Text
                    tag="p"
                    variant="label"
                    size="medium"
                    className="text-text-quartenary line-through"
                  >
                    {formatCurrency(999)}
                  </Text>
                </div>
              </div>
              {/* Tooltip */}
              <div className="rounded-2 shadow-md px-3 py-2 bg-foreground-primary relative">
                <div className="absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-gray-900" />
                <Text
                  tag="p"
                  variant="label"
                  size="medium"
                  className="text-text-primary-on-brand"
                >
                  This is our investment in you. Now its your turn!
                </Text>
              </div>
            </div>
            <div className="mt-[60px] flex flex-col gap-4">
              <Text tag="p" variant="headline" size="large">
                What is included in the test?
              </Text>
              <Text tag="p" variant="body" size="large">
                These tests are included in the CKD diagnostics bundle
              </Text>
              <div className="flex items-center flex-wrap gap-3 pt-6">
                {['eGFR', 'Albuminuria'].map((test) => (
                  <div
                    key={test}
                    className="rounded-full gap-2 px-4 py-3 flex items-center justify-center border border-border-secondary bg-background-secondary"
                  >
                    <LabTestIcon />
                    <Text
                      tag="p"
                      variant="label"
                      size="large"
                      bold={'true'}
                      className={'text-text-primary'}
                    >
                      {test}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="h-16" />
          <Lottie animationData={Separator} autoplay autoPlay loop />
          <div className="h-40" />

          <div
            className={`fixed bottom-0 bg-[#FFFFFFBF] backdrop-blur-2xl w-full max-w-lg lg:max-w-5xl xl:max-w-6xl left-0 right-0 mx-auto py-4 transition-transform duration-500 ${
              showFooter2 ? 'translate-y-full' : 'translate-y-0'
            }`}
          >
            <Button
              title={'Book Diagnostics'}
              type={'goodflip_primary'}
              disabled={false}
              leftIcon={null}
              rightIcon={<ArrowIcon direction="right" color="#FFFFFF" />}
              onClick={onClickBookDiagnostics}
            />
          </div>

          <div
            className={`fixed bottom-0 w-full max-w-lg lg:max-w-5xl xl:max-w-6xl left-0 right-0 mx-auto bg-[#FFFFFFBF] backdrop-blur-2xl py-4 transition-transform duration-500 ${
              showFooter2 ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex flex-row w-full items-center px-4 gap-6">
              <div className="w-auto">
                <Text
                  tag="p"
                  variant="label"
                  size="medium"
                  className={'text-text-primary'}
                >
                  Price for you
                </Text>
                <div className="flex flex-row items-center gap-2">
                  <Text
                    tag="p"
                    variant="label"
                    size="large"
                    bold={'true'}
                    className={'text-text-brand-tertiary'}
                  >
                    {formatCurrency(599)}
                  </Text>
                  <Text
                    tag="p"
                    variant="label"
                    size="medium"
                    className={'text-text-quartenary line-through'}
                  >
                    {formatCurrency(999)}
                  </Text>
                </div>
              </div>
              <Button
                title={'Book Diagnostics'}
                type={'goodflip_primary'}
                className="w-full !mx-0"
                disabled={false}
                leftIcon={null}
                rightIcon={<ArrowIcon direction="right" color="#FFFFFF" />}
                onClick={onClickBookDiagnostics}
              />
            </div>
          </div>
          <LoginBottomSheet
            ref={loginSheetRef}
            buttonTitle={'Book Diagnostics'}
            from={'diagnostics'}
          />
        </div>
      )}
    </MobileLayout>
  )
}

export default memo(DiagnosticPage)
